import { Link } from "react-router-dom";
import {
  ArrowRight,
  Home as HomeIcon,
  Briefcase,
  Building2,
  HardHat,
  BadgeCheck,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { BRAND, STATS, SERVICES, PROFILES, REFERRAL_LIST } from "@/lib/site";
import NewsPreview from "@/components/site/NewsPreview";

const ICONS = { Home: HomeIcon, Briefcase, Building2, HardHat, BadgeCheck };

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden isolate bg-brand-forest">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1720954006045-6b801f7f919e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwYmFuZ2Fsb3JlfGVufDB8fHx8MTc3NzczNzUyN3ww&ixlib=rb-4.1.0&q=85"
            alt="Bangalore corporate skyline"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-forest/95 via-brand-forest/80 to-brand-forest/55" />
          <div className="absolute inset-0 bg-grain opacity-30" />
        </div>

        <div className="container-x relative z-10 pt-24 pb-28 md:pt-32 md:pb-40 text-brand-cream">
          <div className="max-w-3xl animate-fade-up">
            <span className="eyebrow text-brand-gold" data-testid="hero-eyebrow">
              Bangalore · Est. {BRAND.founded}
            </span>
            <h1 className="mt-5 font-outfit font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              From <span className="text-brand-gold italic font-medium">Dream to Deed</span>
              <br />
              we walk every step with you.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-brand-cream/80 leading-relaxed">
              Bangalore-based loan facilitation for salaried professionals,
              business owners and property investors. 15+ lender partners,
              one trusted advisor, transparent fees.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/apply" data-testid="hero-apply-btn" className="btn-primary text-base">
                Start Your Application <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                data-testid="hero-whatsapp-btn"
                href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi Raj, I'd like a free consultation.")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold/40 bg-brand-cream/5 px-6 py-3 text-sm font-semibold text-brand-cream backdrop-blur-md transition-all hover:bg-brand-cream/10 hover:-translate-y-0.5"
              >
                Free Consultation
              </a>
            </div>
          </div>

          {/* stat strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl animate-fade-up">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${i}`}
                className="rounded-2xl border border-brand-cream/15 bg-brand-cream/5 backdrop-blur-md px-5 py-5"
              >
                <div className="font-outfit font-extrabold text-3xl sm:text-4xl text-brand-gold">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-brand-cream/65">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / INTRO */}
      <section className="container-x py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <span className="eyebrow">Partners in Growth</span>
            <h2 className="mt-4 h-display text-3xl sm:text-4xl lg:text-5xl">
              A trusted advisor —<br className="hidden sm:block" />
              not just another loan broker.
            </h2>
          </div>
          <p className="md:col-span-5 text-brand-sage text-base leading-relaxed">
            Since 2018, Swift Financial Solutions has helped Bangalore's
            founders, families and investors access the right debt at the
            right price. We negotiate with banks and NBFCs on your behalf,
            pre-vet your file, and stay your point of contact long after
            disbursal.
          </p>
        </div>

        {/* bento services preview */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-5 auto-rows-[minmax(220px,auto)]">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || HomeIcon;
            const span =
              idx === 0 ? "md:col-span-3 md:row-span-2"
              : idx === 1 ? "md:col-span-3"
              : idx === 2 ? "md:col-span-2"
              : "md:col-span-4";
            return (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                data-testid={`home-service-${s.id}`}
                className={`group card-soft p-6 sm:p-7 flex flex-col justify-between overflow-hidden relative ${span}`}
              >
                <div>
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-forest text-brand-gold">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-outfit font-bold text-2xl text-brand-forest">{s.title}</h3>
                  <p className="mt-3 text-sm text-brand-sage leading-relaxed max-w-md">{s.blurb}</p>
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {s.items.slice(0, 3).map((i) => (
                      <span key={i} className="rounded-full border border-brand-sand bg-brand-sand/40 px-3 py-1 text-xs font-medium text-brand-forest/75">
                        {i}
                      </span>
                    ))}
                  </div>
                  <span className="text-brand-terracotta opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
                    <ArrowRight className="h-5 w-5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-brand-forest text-brand-cream py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-10" />
        <div className="container-x relative">
          <span className="eyebrow text-brand-gold">Who We Serve</span>
          <h2 className="mt-4 font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Three borrower profiles —<br />
            <span className="text-brand-gold italic font-medium">one trusted advisor</span> for all.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PROFILES.map((p, i) => {
              const Icon = ICONS[p.icon] || BadgeCheck;
              return (
                <div
                  key={p.title}
                  data-testid={`profile-${i}`}
                  className="rounded-2xl border border-brand-cream/15 bg-brand-cream/5 backdrop-blur-md p-7 transition-all hover:-translate-y-1 hover:border-brand-gold/40"
                >
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gold text-brand-forest">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-outfit font-bold text-xl">{p.title}</h3>
                  <p className="mt-3 text-sm text-brand-cream/75 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROOF / BUSINESS LOAN BLOCK */}
      <section className="container-x py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <span className="eyebrow">Loan Solutions for Bangalore Business Owners</span>
            <h2 className="mt-4 h-display text-3xl sm:text-4xl">
              48-hour approvals.<br />
              <span className="text-brand-terracotta">15+ lender</span> network.
            </h2>
            <p className="mt-5 text-brand-sage leading-relaxed max-w-lg">
              Whether you're scaling working capital, buying machinery or
              entering a new market — we negotiate term, tenure and pricing
              with the banks and NBFCs that fit your profile best.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Working capital, term loans, MSME funding",
                "Minimum ticket ₹25 Lakh",
                "Pre-vetted file submission to maximise approval probability",
                "Single point of contact, post-disbursal support",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-brand-forest/85">
                  <CheckCircle2 className="h-5 w-5 text-brand-terracotta mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/apply" data-testid="proof-apply-btn" className="btn-primary">
                Apply for a Business Loan
              </Link>
              <Link to="/services#calculator" data-testid="proof-calc-btn" className="btn-secondary">
                Calculate EMI
              </Link>
            </div>
          </div>

          <div className="md:col-span-6 order-1 md:order-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-24 w-24 rounded-2xl bg-brand-gold/30 -z-10" />
              <div className="absolute -bottom-6 -right-6 h-32 w-32 rounded-full bg-brand-terracotta/20 -z-10" />
              <img
                src={BRAND.founderPhotos.editorial}
                alt={`${BRAND.founderName}, founder of Swift Financial Solutions`}
                className="rounded-2xl shadow-[0_30px_80px_-20px_rgba(17,42,34,0.35)] w-full object-cover aspect-[4/5]"
              />
              <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-white/95 backdrop-blur p-4 border border-brand-sand">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-brand-terracotta" />
                  <div>
                    <div className="font-outfit font-bold text-brand-forest">Raj personally reviews every file</div>
                    <div className="text-xs text-brand-sage">₹25L+ business loans · 48-hour approvals*</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO TO REFER */}
      <section className="bg-brand-sand/50 py-20 md:py-24">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <span className="eyebrow">Who To Refer</span>
              <h2 className="mt-4 h-display text-3xl sm:text-4xl">
                If you know someone who fits — we'll take it from there.
              </h2>
              <p className="mt-5 text-brand-sage leading-relaxed">
                A guide for partners and friends — the
                profiles we love being introduced to.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-3">
              {REFERRAL_LIST.map((r, i) => (
                <div
                  key={r}
                  data-testid={`refer-item-${i}`}
                  className="flex items-start gap-3 rounded-xl border border-brand-sand bg-white px-4 py-3.5 text-sm text-brand-forest/85"
                >
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-forest text-brand-gold text-[11px] font-bold">{i + 1}</span>
                  {r}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      <NewsPreview />

      {/* CTA */}
      <section className="container-x pb-20 md:pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-brand-forest p-10 md:p-16 text-brand-cream">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-terracotta/20 blur-2xl" />
          <div className="absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-brand-gold/20 blur-2xl" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="eyebrow text-brand-gold">Memory Hook</span>
              <h2 className="mt-4 font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                "{BRAND.memoryHook}"
              </h2>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link to="/apply" data-testid="bottom-cta-apply" className="btn-primary text-base">
                Talk to Raj <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
